import React, { useRef } from 'react';

import { clsx } from '@freelancelabs/utils';

interface Props {
    /** Code saisi (chaine de 0 a `length` chiffres) */
    value: string;
    onValue: (value: string) => void;
    length?: number;
    disabled?: boolean;
    error?: boolean;
    className?: string;
    /** Libelle du groupe pour les lecteurs d'ecran */
    ariaLabel?: string;
}

/**
 * Saisie de code a usage unique en cases individuelles (style maquette MFA) :
 * auto-avance a la saisie, retour arriere vers la case precedente,
 * collage d'un code complet reparti sur les cases.
 */
const OtpInput = ({ value, onValue, length = 6, disabled, error, className, ariaLabel }: Props) => {
    const refs = useRef<(HTMLInputElement | null)[]>([]);

    const setDigit = (index: number, digit: string) => {
        const chars = Array.from({ length }, (_, i) => value[i] ?? '');
        chars[index] = digit;
        onValue(chars.join('').slice(0, length));
    };

    const handleChange = (index: number, raw: string) => {
        const digits = raw.replace(/\D/g, '');
        if (!digits) {
            setDigit(index, '');
            return;
        }
        if (digits.length > 1) {
            // collage ou remplissage automatique : on repartit depuis la case courante
            const merged = (value.slice(0, index) + digits).replace(/\D/g, '').slice(0, length);
            onValue(merged);
            refs.current[Math.min(merged.length, length - 1)]?.focus();
            return;
        }
        setDigit(index, digits);
        refs.current[Math.min(index + 1, length - 1)]?.focus();
    };

    const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Backspace' && !value[index] && index > 0) {
            refs.current[index - 1]?.focus();
        }
    };

    return (
        <div className={clsx('otp-input', error && 'otp-input--error', className)} role="group" aria-label={ariaLabel}>
            {Array.from({ length }, (_, index) => (
                <input
                    key={index}
                    ref={(node) => {
                        refs.current[index] = node;
                    }}
                    className={clsx('otp-input__case', value[index] && 'otp-input__case--filled')}
                    type="text"
                    inputMode="numeric"
                    maxLength={index === 0 ? length : 1}
                    autoComplete={index === 0 ? 'one-time-code' : 'off'}
                    aria-label={`${index + 1} / ${length}`}
                    value={value[index] ?? ''}
                    disabled={disabled}
                    onChange={(event) => handleChange(index, event.target.value)}
                    onKeyDown={(event) => handleKeyDown(index, event)}
                    onFocus={(event) => event.target.select()}
                />
            ))}
        </div>
    );
};

export default OtpInput;
